/** 请假申请共用表单。
 * HR 假勤页和「我的排班」必须使用同一组字段及校验，避免两个入口的假期类型和天数口径漂移。
 * submitUrl 由入口决定；排班入口使用仅允许提交本人的专用接口，保存后由页面刷新自身数据。
 */
import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/sonner";
import { LEAVE_TYPES, LEAVE_TYPES_EN, inp } from "./hrmsShared";

export default function LeaveRequestDialog({ api, zh, onClose, onSaved, submitUrl = "/hrms/leaves" }) {
  const T = (z, e) => (zh ? z : e);
  const labels = zh ? LEAVE_TYPES : LEAVE_TYPES_EN;
  const [form, setForm] = useState({ type: "annual", startDate: "", endDate: "", days: 1, reason: "" });
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  const submit = async () => {
    // 两个入口共用表单后，如果提交中关闭再打开会重置 busy，可能重复创建申请。
    // 同步标记同时拦住同一轮渲染内的连续点击；请求完成或失败后才允许重试。
    if (submitting.current) return;
    if (!form.startDate || !form.endDate) return toast.error(T("请填写起止日期", "Start and end dates required"));
    submitting.current = true;
    setBusy(true);
    try {
      await api.post(submitUrl, form);
      toast.success(T("申请已提交", "Request submitted"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("提交失败", "Submit failed"));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  const close = () => { if (!submitting.current) onClose(); };

  return <Dialog open onOpenChange={(open) => !open && close()}>
    <DialogContent className="max-w-md" closeDisabled={busy}>
      <DialogHeader><DialogTitle>{T("申请请假", "Request Leave")}</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm">
        <div><label id="hrms-lv-type-label">{T("类型", "Type")}</label><Select value={form.type} onValueChange={(type) => setForm((d) => ({ ...d, type }))}><SelectTrigger aria-labelledby="hrms-lv-type-label" data-testid="hrms-lv-type" className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(LEAVE_TYPES).map((k) => <SelectItem key={k} value={k}>{labels[k]}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label>{T("开始日期", "Start date")} *<input data-testid="hrms-lv-start" type="date" className={inp} value={form.startDate} onChange={(e) => setForm((d) => ({ ...d, startDate: e.target.value }))} /></label>
          <label>{T("结束日期", "End date")} *<input data-testid="hrms-lv-end" type="date" className={inp} value={form.endDate} onChange={(e) => setForm((d) => ({ ...d, endDate: e.target.value }))} /></label>
        </div>
        <label className="block">{T("天数", "Days")}<input data-testid="hrms-lv-days" type="number" min="0.5" step="0.5" className={inp} value={form.days} onChange={(e) => setForm((d) => ({ ...d, days: e.target.value }))} /></label>
        <label className="block">{T("事由", "Reason")}<input data-testid="hrms-lv-reason" className={inp} value={form.reason} onChange={(e) => setForm((d) => ({ ...d, reason: e.target.value }))} /></label>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <button onClick={close} disabled={busy} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
          <button data-testid="hrms-lv-submit-btn" disabled={busy} onClick={submit} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("提交申请", "Submit")}</button>
        </div>
      </div>
    </DialogContent>
  </Dialog>;
}
